<template>
  <v-dialog v-model="dialog" max-width="746">
    <template v-slot:activator="{ on }">
      <div v-on="on">
        <slot />
      </div>
    </template>
    <v-card class="used-book-selection-dialog-card-wrapper">
      <v-icon class="ml-auto close-button" @click="close()">
        mdi-close
      </v-icon>

      <!-- Mobile -->
      <div v-if="$breakpoint.is.xs" class="d-flex flex-column">
        <div class="overflow-auto flex-grow-1 pb-3">
          <div v-if="showFilter">
            <div>
              <div v-a11y-clickable class="d-flex align-center clickable font-x-large pl-2 pb-2" @click="showFilter = false">
                <v-icon large color="secondary">
                  mdi-chevron-left
                </v-icon>
                Filter
              </div>
              <v-expansion-panels v-model="expanded" accordion multiple>
                <v-expansion-panel>
                  <v-expansion-panel-header :expand-icon="expanded.indexOf(0) >= 0 ? 'mdi-minus' : 'mdi-plus'">
                    Condition
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="p-0">
                    <v-list dense>
                      <v-list-item
                        v-for="condition of conditions"
                        :key="condition"
                        class="filter-list-item"
                      >
                        <base-checkbox
                          v-model="filters.conditions"
                          :label="(condition ? condition : 'Not Specified') + ' (' + usedProducts.filter(x => x.condition === condition).length + ')'"
                          :val="condition"
                        />
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
                <v-expansion-panel>
                  <v-expansion-panel-header :expand-icon="expanded.indexOf(1) >= 0 ? 'mdi-minus' : 'mdi-plus'">
                    Format
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="p-0">
                    <v-list dense>
                      <v-list-item
                        v-for="format of formats"
                        :key="format"
                        class="filter-list-item"
                      >
                        <base-checkbox
                          v-model="filters.formats"
                          :label="format + ' (' + usedProducts.filter(x => x.type === format).length + ')'"
                          :val="format"
                        />
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>

          <div v-if="!showFilter" class="product-wrapper">
            <div class="product-details d-flex">
              <div class="product-featured-image">
                <base-image
                  :source="product.featuredImageUrl | fullImagePath('S')"
                  :alt="product.title + ' Cover Image'"
                  max-width="69"
                  contain
                  class="mr-4"
                />
              </div>
              <div class="product-information d-flex flex-column">
                <h1 class="font-weight-medium">
                  {{ product.title }} (Used)
                </h1>
                <span v-if="product.author" class="font-medium">by <span class="primary--text">{{ product.author }}</span></span>
                <div class="font-weight-bold font-small">
                  {{ usedProducts.length }}&nbsp;items
                </div>
              </div>
            </div>
            <div class="filter-container" @click="filter($event)">
              <base-select label="Filter" class="background-white" hide-details :uppercase="false" />
            </div>
            <div v-if="!filteredUsedProducts.length" class="pa-5 text-center">
              No results found
            </div>
            <div
              v-for="(p, i) of filteredUsedProducts"
              :key="'selection-item_' + p.id"
              class="variant d-flex pt-2"
              :class="{ 'first': i === 0 }"
            >
              <base-quantity-select
                :quantity="getSelectedQuantity(p)"
                :max="p.quantityAvailable"
                include-zero
                outlined
                class="mt-2"
                @change="updateSelected($event, p)"
              />
              <div class="pl-4 fx-1">
                <div class="d-flex flex-row">
                  <div class="price font-weight-bold accent--text pr-2">
                    {{ p.basePrice | formatCurrency }}
                  </div>
                  <div v-if="p.basePrice < p.msrp">
                    <div class="product-msrp font-weight-light gray--text">
                      {{ p.msrp | formatCurrency }}
                    </div>
                    <div class="discount font-weight-light accent--text">
                      Save {{ productDiscount(p.basePrice, p.msrp) }}%
                    </div>
                  </div>
                </div>
                <span class="font-medium font-weight-bold">
                  Format: {{ p.type }} <br>
                  <span v-if="p.condition">
                    Condition: {{ p.condition }}
                    <v-tooltip v-if="conditionDescriptionByCondition(p.condition)" top>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-icon color="primary" small>
                            mdi-information-outline
                          </v-icon>
                        </span>
                      </template>
                      <prismic-rich-text
                        :field="conditionDescriptionByCondition(p.condition)"
                        class="no-margin"
                      />
                    </v-tooltip>
                  </span>
                </span>
                <p class="font-small">
                  {{ p.conditionDescription }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="actions flex-shrink-0">
          <v-chip
            v-for="(condition, i) of filters.conditions"
            :key="condition"
            class="mr-1 mb-1"
            close
            close-icon="mdi-close"
            label
            color="gray"
            text-color="white"
            @click:close="filters.conditions.splice(i, 1)"
          >
            {{ condition ? condition : 'Not Specified' }}
          </v-chip>
          <v-chip
            v-for="(format, i) of filters.formats"
            :key="format"
            class="mr-1 mb-1"
            close
            close-icon="mdi-close"
            label
            color="gray"
            text-color="white"
            @click:close="filters.formats.splice(i, 1)"
          >
            {{ format }}
          </v-chip>
          <span v-if="anyFilters">({{ filteredUsedProducts.length }})</span>

          <div class="d-flex align-center pt-1">
            <div class="fx-1">
              <base-button
                v-if="!showFilter"
                text="Add To Cart"
                block
                :disabled="!selected.length"
                @click="addToCart()"
              />
              <base-button v-if="showFilter" text="Apply" block @click="showFilter = false" />
            </div>
            <span
              v-if="anyFilters"
              v-a11y-clickable
              class="pl-5 primary--text clickable text-underline"
              @click="clearFilters"
            >Clear&nbsp;filter(s)
            </span>
            <span
              v-if="!anyFilters"
              v-a11y-clickable
              class="pl-5 primary--text clickable text-underline"
              @click="selected = []"
            >Clear
            </span>
          </div>
        </div>
      </div>

      <!-- Tablet & Up -->
      <div v-if="$breakpoint.is.smAndUp" class="d-flex">
        <div class="filter-wrapper">
          <h2 class="font-x-large">
            Filter <span v-a11y-clickable class="primary--text clickable font-small ml-4" @click="clearFilters()">Clear All</span>
          </h2>
          <v-list class="filter-list" dense>
            <v-subheader class="filter-list-section-header">
              Condition
            </v-subheader>
            <v-list-item
              v-for="condition of conditions"
              :key="condition"
              class="filter-list-item"
            >
              <base-checkbox v-model="filters.conditions" :label="condition ? condition : 'Not Specified'" :val="condition" />
            </v-list-item>
            <v-subheader class="filter-list-section-header">
              Format
            </v-subheader>
            <v-list-item
              v-for="format of formats"
              :key="format"
              class="filter-list-item"
            >
              <base-checkbox v-model="filters.formats" :label="format" :val="format" />
            </v-list-item>
          </v-list>
        </div>
        <div class="product-wrapper">
          <div class="overflow-auto">
            <div class="product-details d-flex">
              <div class="product-featured-image">
                <base-image
                  :source="product.featuredImageUrl | fullImagePath('S')"
                  :alt="product.title + ' Cover Image'"
                  max-width="69"
                  contain
                  class="mr-4"
                />
              </div>
              <div class="product-information d-flex flex-column">
                <h1 class="font-weight-medium">
                  {{ product.title }} (Used) <span class="font-large pl-3">{{ usedProducts.length }}&nbsp;items</span>
                </h1>
                <span v-if="product.author" class="font-medium">by <span class="primary--text">{{ product.author }}</span></span>
              </div>
            </div>
            <div class="selection-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!filteredUsedProducts.length">
                    <td class="font-medium text-center pa-4" colspan="3">
                      No results found.
                    </td>
                  </tr>
                  <tr
                    v-for="p of filteredUsedProducts"
                    :key="'selection-table-item_' + p.id"
                    class="table-item-row"
                  >
                    <td>
                      <base-quantity-select
                        :quantity="getSelectedQuantity(p)"
                        :max="p.quantityAvailable"
                        include-zero
                        outlined
                        @change="updateSelected($event, p)"
                      />
                    </td>
                    <td>
                      <div class="variant-pricing-wrapper">
                        <div class="price font-weight-bold accent--text">
                          {{ p.basePrice | formatCurrency }}
                        </div>
                        <div
                          v-if="p.basePrice < p.msrp"
                          class="product-msrp font-weight-medium gray--text"
                        >
                          {{ p.msrp | formatCurrency }}
                        </div>
                        <div
                          v-if="p.basePrice < p.msrp"
                          class="discount font-weight-medium accent--text"
                        >
                          Save {{ productDiscount(p.basePrice, p.msrp) }}%
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex">
                        <!--<v-img
                          v-if="p.featuredImageUrl"
                          :src="p.featuredImageUrl | fullImagePath('S')"
                          :alt="p.title + ' Cover Image'"
                          max-width="40"
                          contain
                          class="mr-4"
                        />-->
                        <div>
                          <span class="font-medium font-weight-bold">
                            Format: {{ p.type }} <br>
                            <span v-if="p.condition">
                              Condition: {{ p.condition }}
                              <v-tooltip v-if="conditionDescriptionByCondition(p.condition)" top>
                                <template v-slot:activator="{ on }">
                                  <span v-on="on">
                                    <v-icon color="primary" small>
                                      mdi-information-outline
                                    </v-icon>
                                  </span>
                                </template>
                                <prismic-rich-text
                                  :field="conditionDescriptionByCondition(p.condition)"
                                  class="no-margin"
                                />
                              </v-tooltip>
                            </span>
                          </span>
                          <p class="font-small">
                            {{ p.conditionDescription }}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="actions px-8 pt-4">
            <base-button text="Add To Cart" block :disabled="!selected.length" @click="addToCart()" />
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import BaseButton from '@/components/base/base-button'
import { calculateDiscount } from '@/helpers/utilities'
import BaseQuantitySelect from '@/components/base/base-quantity-select'
import { LAYOUT_TYPES } from '@/store/layout'

export default {
  name: 'UsedBookSelectionDialog',
  components: { BaseQuantitySelect, BaseButton },
  props: {
    product: {
      type: Object,
      required: true
    },
    usedProducts: {
      type: Array,
      required: true
    },
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      selected: [],
      filters: {
        conditions: [],
        formats: []
      },
      showFilter: false,
      expanded: [0, 1]
    }
  },
  computed: {
    ...mapGetters({
      conditionDescriptionByCondition: LAYOUT_TYPES.getters.conditionDescriptionByCondition
    }),
    conditions () {
      return [...new Set(this.usedProducts.map(x => x.condition))].sort()
    },
    formats () {
      return [...new Set(this.usedProducts.map(x => x.type))].sort()
    },
    lowestPriceUsedOption () {
      return Math.min(...this.usedProducts.map(x => x.basePrice))
    },
    filteredUsedProducts () {
      return this.usedProducts.filter(x =>
        (!this.filters.conditions.length || this.filters.conditions.some(y => y === x.condition)) &&
        (!this.filters.formats.length || this.filters.formats.some(y => y === x.type))
      )
    },
    anyFilters () {
      return this.filters.conditions.length || this.filters.formats.length
    }
  },
  watch: {
    showDialog () {
      this.selected = []
      this.filters = {
        conditions: [],
        formats: []
      }
      this.dialog = this.showDialog
    }
  },
  destroyed () {
    this.$emit('closed')
  },
  methods: {
    filter (event) {
      this.showFilter = true
      event.stopPropagation()
    },
    productDiscount (sale, msrp) {
      return calculateDiscount(sale, msrp)
    },
    close () {
      this.dialog = false
      this.$emit('closed')
    },
    addToCart () {
      this.dialog = false
      this.$emit('closed', this.selected)
    },
    clearFilters () {
      this.filters = {
        conditions: [],
        formats: []
      }
    },
    updateSelected (quantity, product) {
      const index = this.selected.findIndex(x => x.product.id === product.id)
      if (index >= 0) {
        if (quantity === 0) {
          this.selected.splice(index, 1)
        } else {
          this.selected[index].quantityToOrder = quantity
        }
      } else {
        this.selected.push({
          product,
          quantityToOrder: quantity
        })
      }
    },
    getSelectedQuantity (product) {
      const s = this.selected.find(x => x.product.id === product.id)
      return s ? s.quantityToOrder : 0
    }
  }
}
</script>

<style lang="scss" scoped>
  @import './scss/variables';

  p {
    margin-bottom: 0;
  }

  ::v-deep .base-quantity-select .v-input {
    max-width: 80px !important;
  }

  .used-book-selection-dialog-card-wrapper {
    font-size: 10px;
    padding: 3.2em 0 3.2em 3.2em;
    height: 100%;
    max-height: 80vh;

    .close-button {
      position: absolute;
      top: 7px;
      right: 7px;
    }

    .filter-wrapper {
      flex: 0 1 140px;
      border-right: 1px solid rgba($charcoal-grey, 0.5);

      .filter-list {
        font-size: 10px;

        .filter-list-section-header {
          font-size: 1.7em;
          font-weight: $font-weight-extra;
          color: $charcoal-grey;
          padding: 0;
          margin-top: 10px;
        }

        .filter-list-item {
          padding: 0;
          max-height: 20px;
          font-size: 14px !important;
          font-weight: bold;
        }
      }
    }

    .product-wrapper {
      flex: 2;

      .overflow-auto {
        max-height: 60vh;
        padding: 0 2em;
      }

      .selection-table-wrapper {
        padding-top: 2em;
        width: 100%;

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th {
          text-align: left;
          font-size: 1.4em;
          font-weight: $font-weight-bold;
          padding: 1em 2em 1em 0;
        }

        .table-item-row {
          td {
            height: 100%;
            padding: 2em 2em 2em 0;
            border-top: 1px solid rgba($charcoal-grey, 0.5);
            border-bottom: 1px solid rgba($charcoal-grey, 0.5);
          }
        }

        .variant-pricing-wrapper {
          min-width: 15em;

          .price {
            font-size: 3.5em;
          }

          .product-msrp {
            font-size: 1.7em;
            text-decoration: line-through;
          }

          .discount {
            font-size: 1.7em;
            line-height: 16px;
          }
        }
      }
    }
  }

  @media screen and (max-width: $breakpoint-sm) {
    .used-book-selection-dialog-card-wrapper {
      padding: 3em 0 2em 2em;
    }
  }

  @media screen and (max-width: $breakpoint-xs) {
    .flex-column {
      max-height: 75vh;
    }

    .v-expansion-panels {
      border-radius: 0;

      .v-expansion-panel {
        &:before {
          box-shadow: none;
        }

        &:after {
          border-top: none;
        }

        .v-expansion-panel-header {
          background-color: #f3f3f3;
          font-weight: $font-weight-extra;
          font-size: $font-medium;
        }
      }
    }

    .used-book-selection-dialog-card-wrapper {
      padding: 2.5em 0 0 0;

      .close-button {
        position: absolute;
        top: 4px;
        right: 4px;
      }

      .product-details {
        padding-left: 2.5em;
      }

      .filter-container {
        background-color: $light-grey;
        margin-top: 1em;
        padding: 1.6em 2em;
      }

      .product-wrapper {
        padding: 0;

        .product-information {
          h1 {
            font-size: $font-medium;
          }
        }

        .price {
          font-size: 19px;
        }

        .product-msrp {
          text-decoration: line-through;
        }

        .product-msrp, .discount {
          font-size: 11px;
        }

        .variant {
          margin: 1em 2em 1em 2em;

          &:not(.first) {
            border-top: 1px solid $light-grey;
          }
        }
      }

      .actions {
        padding: 2em;
        box-shadow: 0 -1px 5px 0 rgba(0, 0, 0, 0.2);
        z-index: 2;
        position: relative;
      }
    }
  }
</style>
